/**
 * usePlatformConfig
 * -----------------
 * Resolves the active platform configuration for a given memorial.
 *
 * Priority order:
 *   1. Memorial-specific override (stored in localStorage by MemorialPlatformSettings)
 *   2. Global admin defaults (stored in localStorage by WebinarIntegrations / StreamingIntegrations)
 *   3. Hard-coded sensible fallbacks
 */

const WEBINAR_PLATFORMS = [
  { id: 'zoom',   label: 'Zoom',            emoji: '🔵' },
  { id: 'gmeet',  label: 'Google Meet',     emoji: '🟢' },
  { id: 'teams',  label: 'Microsoft Teams', emoji: '🟣' },
  { id: 'jitsi',  label: 'Jitsi Meet',      emoji: '🟠' },
  { id: 'native', label: 'Native Stream',   emoji: '🔴' },
];

const STREAMING_PLATFORMS = [
  { id: 'youtube',   label: 'YouTube Live',   emoji: '▶️' },
  { id: 'facebook',  label: 'Facebook Live',  emoji: '📘' },
  { id: 'instagram', label: 'Instagram Live', emoji: '📸' },
  { id: 'twitch',    label: 'Twitch',         emoji: '🟣' },
];

const safeLoad = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
};

/**
 * Returns the fully-resolved platform config for a memorial.
 * Call this from any component that needs to know which platform to use.
 *
 * @param {string} memorialId
 * @returns {{
 *   webinar: { platformId, label, emoji, keys, isOverride },
 *   streaming: { platformId, label, emoji, keys, rtmpBase, isOverride }
 * }}
 */
export function resolvePlatformConfig(memorialId) {
  // ── Layer 1: Memorial-specific settings ─────────────────────────────────────
  const memSettings = safeLoad(`em_memorial_platform_${memorialId}`);

  // ── Layer 2: Global admin settings ──────────────────────────────────────────
  const globalWebinar   = safeLoad('em_webinar_integrations');
  const globalStreaming  = safeLoad('em_streaming_integrations');

  // ── Resolve WEBINAR ──────────────────────────────────────────────────────────
  let webinarPlatformId, webinarKeys, webinarIsOverride;

  if (memSettings.overrideWebinar && memSettings.webinarDefault) {
    webinarPlatformId  = memSettings.webinarDefault;
    webinarKeys        = memSettings.webinarKeys?.[webinarPlatformId] || {};
    webinarIsOverride  = true;
  } else {
    webinarPlatformId  = globalWebinar.defaultPlatform || 'zoom';
    webinarKeys        = globalWebinar.keys?.[webinarPlatformId] || {};
    webinarIsOverride  = false;
  }

  const webinarMeta = WEBINAR_PLATFORMS.find(p => p.id === webinarPlatformId) || WEBINAR_PLATFORMS[0];

  // ── Resolve STREAMING ────────────────────────────────────────────────────────
  const RTMP_BASES = {
    youtube:   'rtmp://a.rtmp.youtube.com/live2/',
    facebook:  'rtmps://live-api-s.facebook.com:443/rtmp/',
    instagram: 'rtmps://edgetee-upload.facebook.com:443/rtmp/',
    twitch:    'rtmp://live.twitch.tv/app/',
  };

  let streamingPlatformId, streamingKeys, streamingIsOverride;

  if (memSettings.overrideStreaming && memSettings.streamingDefault) {
    streamingPlatformId = memSettings.streamingDefault;
    streamingKeys       = memSettings.streamingKeys?.[streamingPlatformId] || {};
    streamingIsOverride = true;
  } else {
    streamingPlatformId = globalStreaming.defaultPlatform || 'youtube';
    streamingKeys       = globalStreaming.keys?.[streamingPlatformId] || {};
    streamingIsOverride = false;
  }

  const streamingMeta = STREAMING_PLATFORMS.find(p => p.id === streamingPlatformId) || STREAMING_PLATFORMS[0];
  const rtmpBase      = RTMP_BASES[streamingPlatformId] || RTMP_BASES.youtube;

  return {
    webinar: {
      platformId:  webinarPlatformId,
      label:       webinarMeta.label,
      emoji:       webinarMeta.emoji,
      keys:        webinarKeys,
      isOverride:  webinarIsOverride,
    },
    streaming: {
      platformId:  streamingPlatformId,
      label:       streamingMeta.label,
      emoji:       streamingMeta.emoji,
      keys:        streamingKeys,
      rtmpBase,
      rtmpFull:    streamingKeys.streamKey ? `${rtmpBase}${streamingKeys.streamKey}` : null,
      isOverride:  streamingIsOverride,
    },
  };
}

/**
 * React hook wrapper — re-resolves on storage changes.
 */
import { useState, useEffect } from 'react';

export function usePlatformConfig(memorialId) {
  const [config, setConfig] = useState(() => resolvePlatformConfig(memorialId));

  useEffect(() => {
    const refresh = () => setConfig(resolvePlatformConfig(memorialId));
    window.addEventListener('memorial_platform_settings_updated', refresh);
    window.addEventListener('webinar_integrations_updated', refresh);
    window.addEventListener('streaming_integrations_updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('memorial_platform_settings_updated', refresh);
      window.removeEventListener('webinar_integrations_updated', refresh);
      window.removeEventListener('streaming_integrations_updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [memorialId]);

  return config;
}